export type OcrTimelineEvent = {
  at: string
  eventType: string
  details: Record<string, unknown>
}

export type OcrCallStatus = 'ok' | 'failed' | 'open'

export type OcrCallSpan = {
  page: number
  durationMs: number
  status: OcrCallStatus
}

export type OcrCallStats = {
  finishedCount: number
  openCount: number
  failedCount: number
  /** Finished calls only (completed + failed). */
  longestMs: number
  longestPage: number
  averageMs: number
  p95Ms: number
  p99Ms: number
}

function asPageNumber(details: Record<string, unknown>): number | null {
  const value = details.page_number
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

export function parseEventTimeMs(at: string): number | null {
  const ms = Date.parse(at)
  return Number.isFinite(ms) ? ms : null
}

/**
 * Nearest-rank percentile on a pre-sorted ascending array.
 * p in (0, 100]; empty array returns 0.
 */
export function percentileNearestRank(sortedAscending: number[], p: number): number {
  if (sortedAscending.length === 0) {
    return 0
  }
  const rank = Math.ceil((p / 100) * sortedAscending.length)
  const index = Math.min(sortedAscending.length - 1, Math.max(0, rank - 1))
  return sortedAscending[index]
}

function buildOcrCallSpans(events: OcrTimelineEvent[]): OcrCallSpan[] {
  const timed = events
    .map((event) => {
      const ms = parseEventTimeMs(event.at)
      if (ms == null) {
        return null
      }
      return { ...event, ms }
    })
    .filter((event): event is OcrTimelineEvent & { ms: number } => event != null)
    .sort((left, right) => left.ms - right.ms || left.eventType.localeCompare(right.eventType))

  if (timed.length === 0) {
    return []
  }

  let terminalMs: number | null = null
  const openByPage = new Map<number, number>()
  const spans: OcrCallSpan[] = []

  for (const event of timed) {
    const page = asPageNumber(event.details)

    if (event.eventType === 'job_completed' || event.eventType === 'job_failed') {
      terminalMs = event.ms
    }

    if (event.eventType === 'page_started' && page != null) {
      openByPage.set(page, event.ms)
      continue
    }

    if (
      (event.eventType === 'page_completed' || event.eventType === 'page_failed') &&
      page != null
    ) {
      const startMs = openByPage.get(page)
      if (startMs != null) {
        openByPage.delete(page)
        spans.push({
          page,
          durationMs: Math.max(0, event.ms - startMs),
          status: event.eventType === 'page_failed' ? 'failed' : 'ok',
        })
      }
    }
  }

  const endMs = terminalMs ?? timed[timed.length - 1].ms
  for (const [page, startMs] of openByPage) {
    spans.push({
      page,
      durationMs: Math.max(0, endMs - startMs),
      status: 'open',
    })
  }

  return spans
}

/**
 * OCR call duration stats from RAG job events.
 * Longest / average / p95 / p99 use finished calls only (completed + failed).
 * Open calls (still in flight at job end) are counted separately and excluded
 * from latency percentiles so censored durations do not inflate the distribution.
 */
export function buildOcrCallStats(events: OcrTimelineEvent[]): OcrCallStats | null {
  const spans = buildOcrCallSpans(events)
  if (spans.length === 0) {
    return null
  }

  const finished = spans.filter((span) => span.status !== 'open')
  const openCount = spans.length - finished.length
  const failedCount = finished.filter((span) => span.status === 'failed').length

  if (finished.length === 0) {
    return {
      finishedCount: 0,
      openCount,
      failedCount: 0,
      longestMs: 0,
      longestPage: 0,
      averageMs: 0,
      p95Ms: 0,
      p99Ms: 0,
    }
  }

  const durations = finished.map((span) => span.durationMs)
  const sorted = [...durations].sort((left, right) => left - right)
  const sum = durations.reduce((acc, value) => acc + value, 0)
  let longestMs = finished[0].durationMs
  let longestPage = finished[0].page
  for (const span of finished) {
    if (span.durationMs > longestMs) {
      longestMs = span.durationMs
      longestPage = span.page
    }
  }

  return {
    finishedCount: finished.length,
    openCount,
    failedCount,
    longestMs,
    longestPage,
    averageMs: sum / finished.length,
    p95Ms: percentileNearestRank(sorted, 95),
    p99Ms: percentileNearestRank(sorted, 99),
  }
}

export function formatOcrDurationMs(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) {
    return '—'
  }
  if (ms < 1000) {
    return `${Math.round(ms)} ms`
  }
  if (ms < 10_000) {
    return `${(ms / 1000).toFixed(2)} s`
  }
  return `${(ms / 1000).toFixed(1)} s`
}
